import { ReactNode } from 'react';

import { Stream, Streamable } from '@/vibes/soul/lib/streamable';
import { Price, PriceLabel } from '@/vibes/soul/primitives/price-label';
import { AnimatedUnderline } from '@/vibes/soul/primitives/animated-underline';
import { Rating } from '@/vibes/soul/primitives/rating';
import * as Skeleton from '@/vibes/soul/primitives/skeleton';
import { type Breadcrumb, Breadcrumbs } from '@/vibes/soul/sections/breadcrumbs';
import {
  ProductGallery,
  ProductGalleryLoadMoreAction,
} from '@/vibes/soul/sections/product-detail/product-gallery';
import { ReviewForm, SubmitReviewAction } from '@/vibes/soul/sections/reviews/review-form';

import {
  BackorderDisplayData,
  ProductDetailForm,
  ProductDetailFormAction,
  StockDisplayData,
} from './product-detail-form';
import { RatingLink } from './rating-link';
import { Field } from './schema';
import { FitmentBanner } from '~/components/vehicle-filter-form/components/product/fitment-banner';

interface ProductDetailProduct {
  id: string;
  title: string;
  href: string;
  images: Streamable<{
    images: Array<{ src: string; alt: string }>;
    pageInfo?: { hasNextPage: boolean; endCursor: string | null };
  }>;
  price?: Streamable<Price | null>;
  subtitle?: string;
  badge?: string;
  rating?: Streamable<number | null>;
  reviewsEnabled?: boolean;
  showRating?: boolean;
  numberOfReviews?: number;
  summary?: Streamable<string>;
  description?: Streamable<string | ReactNode | null>;
  warranty?: Streamable<string | null>;
  accordions?: Streamable<
    Array<{
      title: string;
      content: ReactNode;
    }>
  >;
  minQuantity?: Streamable<number | null>;
  maxQuantity?: Streamable<number | null>;
  stockDisplayData?: Streamable<StockDisplayData | null>;
  backorderDisplayData?: Streamable<BackorderDisplayData | null>;
}

export interface ProductDetailProps<F extends Field> {
  breadcrumbs?: Streamable<Breadcrumb[]>;
  product: Streamable<ProductDetailProduct | null>;
  action: ProductDetailFormAction<F>;
  fields: Streamable<F[]>;
  quantityLabel?: string;
  incrementLabel?: string;
  decrementLabel?: string;
  emptySelectPlaceholder?: string;
  ctaLabel?: Streamable<string | null>;
  ctaDisabled?: Streamable<boolean | null>;
  prefetch?: boolean;
  thumbnailLabel?: string;
  additionalInformationTitle?: string;
  additionalActions?: ReactNode;
  reviewFormEmailLabel?: string;
  reviewFormModalTitle?: string;
  reviewFormNameLabel?: string;
  reviewFormRatingLabel?: string;
  reviewFormReviewLabel?: string;
  reviewFormSubmitLabel?: string;
  reviewFormTitleLabel?: string;
  reviewFormAction: SubmitReviewAction;
  user: Streamable<{ email: string; name: string }>;
  loadMoreImagesAction?: ProductGalleryLoadMoreAction;
  recaptchaSiteKey?: string;
  descriptionTitle?: string;
  warrantyTitle?: string;
}

// eslint-disable-next-line valid-jsdoc
/**
 * This component supports various CSS variables for theming. Here's a comprehensive list, along
 * with their default values:
 *
 * ```css
 * :root {
 *   --product-detail-border: hsl(var(--contrast-100));
 *   --product-detail-subtitle-font-family: var(--font-family-mono);
 *   --product-detail-title-font-family: var(--font-family-heading);
 *   --product-detail-primary-text: hsl(var(--foreground));
 *   --product-detail-secondary-text:  hsl(var(--contrast-500));
 * }
 * ```
 */
export function ProductDetail<F extends Field>({
  product: streamableProduct,
  action,
  fields: streamableFields,
  breadcrumbs,
  quantityLabel,
  incrementLabel,
  decrementLabel,
  emptySelectPlaceholder,
  ctaLabel: streamableCtaLabel,
  ctaDisabled: streamableCtaDisabled,
  prefetch,
  thumbnailLabel,
  additionalInformationTitle = 'Additional information',
  descriptionTitle = 'Description',
  warrantyTitle = 'Warranty',
  additionalActions,
  reviewFormEmailLabel,
  reviewFormModalTitle,
  reviewFormNameLabel,
  reviewFormRatingLabel,
  reviewFormReviewLabel,
  reviewFormSubmitLabel,
  reviewFormTitleLabel,
  reviewFormAction,
  user,
  loadMoreImagesAction,
  recaptchaSiteKey,
}: ProductDetailProps<F>) {
  return (
    <section className="@container">
      <div className="group/product-detail mx-auto w-full max-w-screen-2xl px-6 py-4 @5xl:pb-8">
        {breadcrumbs && (
          <div className="group/breadcrumbs mb-4 @5xl:mb-8">
            <Breadcrumbs breadcrumbs={breadcrumbs} />
          </div>
        )}
        <Stream fallback={<ProductDetailSkeleton />} value={streamableProduct}>
          {(product) =>
            product && (
              <>
                <FitmentBanner productId={product.id} />
                <div className="grid grid-cols-1 items-stretch @2xl:gap-x-8 gap-y-8 @2xl:grid-cols-2">
                  <div className="group/product-gallery hidden @2xl:block">
                    <Stream fallback={<ProductGallerySkeleton />} value={product.images}>
                      {(imagesData) => (
                        <ProductGallery
                          images={imagesData.images}
                          loadMoreAction={loadMoreImagesAction}
                          pageInfo={imagesData.pageInfo}
                          productId={Number(product.id)}
                          fit="cover"
                          aspectRatio="1:1"
                        />
                      )}
                    </Stream>
                  </div>
                  {/* Product Details */}
                  <div className="text-[var(--product-detail-primary-text,hsl(var(--foreground)))]">
                    {Boolean(product.subtitle) && (
                      <p className="font-[family-name:var(--product-detail-subtitle-font-family,var(--font-family-mono))] font-semibold uppercase text-[var(--product-detail-subtitle-color,hsl(var(--muted-600)))]">
                        {product.subtitle}
                      </p>
                    )}
                    <h1 className="mb-3 mt-2 font-[family-name:var(--product-detail-title-font-family,var(--font-family-base))] text-3xl font-black leading-none @xl:mb-4 @4xl:text-4xl">
                      {product.title}
                    </h1>
                    {product.reviewsEnabled && (
                      <div className="group/product-rating">
                        <ReviewForm
                          action={reviewFormAction}
                          formEmailLabel={reviewFormEmailLabel}
                          formModalTitle={reviewFormModalTitle}
                          formNameLabel={reviewFormNameLabel}
                          formRatingLabel={reviewFormRatingLabel}
                          formReviewLabel={reviewFormReviewLabel}
                          formSubmitLabel={reviewFormSubmitLabel}
                          formTitleLabel={reviewFormTitleLabel}
                          productId={Number(product.id)}
                          recaptchaSiteKey={recaptchaSiteKey}
                          streamableImages={product.images}
                          streamableProduct={{ name: product.title }}
                          streamableUser={user}
                          trigger={
                            <AnimatedUnderline className="cursor-pointer">
                              Write a review
                            </AnimatedUnderline>
                          }
                        />
                      </div>
                    )}
                    {product.showRating && (
                      <div className="group/product-rating">
                        <Stream
                          fallback={<RatingSkeleton />}
                          value={Streamable.all([product.rating, product.numberOfReviews])}
                        >
                          {([rating, numberOfReviews]) => (
                            <RatingLink
                              numberOfReviews={numberOfReviews ?? 0}
                              rating={rating ?? 0}
                              scrollTargetId="reviews"
                            />
                          )}
                        </Stream>
                      </div>
                    )}
                    <div className="group/product-gallery mb-8 @2xl:hidden">
                      <Stream fallback={<ProductGallerySkeleton />} value={product.images}>
                        {(imagesData) => (
                          <ProductGallery
                            images={imagesData.images}
                            loadMoreAction={loadMoreImagesAction}
                            pageInfo={imagesData.pageInfo}
                            productId={Number(product.id)}
                            thumbnailLabel={thumbnailLabel}
                            fit="cover"
                            aspectRatio="1:1"
                          />
                        )}
                      </Stream>
                    </div>
                    <div className="group/product-price">
                      <Stream fallback={<PriceLabelSkeleton />} value={product.price}>
                        {(price) => (
                            <PriceLabel className="text-xl flex flex-col mb-4 @4xl:mb-0 @xl:text-2xl @5xl:text-3xl font-heading" price={price ?? ''} />
                        )}
                      </Stream>
                    </div>
                    <div className="group/product-summary">
                      <Stream fallback={<ProductSummarySkeleton />} value={product.summary}>
                        {(summary) =>
                          Boolean(summary) && (
                            <p className="text-[var(--product-detail-secondary-text,hsl(var(--contrast-500)))]">
                              {summary}
                            </p>
                          )
                        }
                      </Stream>
                    </div>
                    <div className="group/product-detail-form">
                      <Stream
                        fallback={<ProductDetailFormSkeleton />}
                        value={Streamable.all([
                          streamableFields,
                          streamableCtaLabel,
                          streamableCtaDisabled,
                          product.minQuantity,
                          product.maxQuantity,
                          product.stockDisplayData,
                          product.backorderDisplayData,
                        ])}
                      >
                        {([
                          fields,
                          ctaLabel,
                          ctaDisabled,
                          minQuantity,
                          maxQuantity,
                          stockDisplayData,
                          backorderDisplayData,
                        ]) => (
                          <ProductDetailForm
                            action={action}
                            additionalActions={additionalActions}
                            backorderDisplayData={backorderDisplayData ?? undefined}
                            ctaDisabled={ctaDisabled ?? undefined}
                            ctaLabel={ctaLabel ?? undefined}
                            decrementLabel={decrementLabel}
                            emptySelectPlaceholder={emptySelectPlaceholder}
                            fields={fields}
                            incrementLabel={incrementLabel}
                            maxQuantity={maxQuantity ?? undefined}
                            minQuantity={minQuantity ?? undefined}
                            prefetch={prefetch}
                            productId={product.id}
                            quantityLabel={quantityLabel}
                            stockDisplayData={stockDisplayData ?? undefined}
                          />
                        )}
                      </Stream>
                    </div>
                    <h2 className="sr-only">{additionalInformationTitle}</h2>
                    <div className="group/product-accordion">
                      <Stream fallback={<ProductInfoGridSkeleton />} value={product.accordions}>
                        {(accordions) =>
                          accordions && (
                            <>
                              <h2 className="sr-only">{additionalInformationTitle}</h2>
                              <div className="group/product-info-grid">
                                <div className="pt-4">
                                  {accordions.map((accordion, index) => (
                                      <div key={index} className="space-y-3">
                                        <h3 className="font-heading text-2xl font-semibold text-foreground">
                                          {accordion.title}
                                        </h3>
                                        <div className="text-contrast-600">
                                          {accordion.content}
                                        </div>
                                      </div>
                                  ))}
                                </div>
                              </div>
                            </>
                          )
                        }
                      </Stream>
                    </div>
                  </div>

                  <div className="group/product-description col-span-2">
                    <Stream fallback={<ProductDescriptionSkeleton />} value={product.description}>
                      {(description) =>
                        Boolean(description) && (
                          <div className="group/product-description col-span-2">
                            <div className="prose max-w-none [&>div>*:first-child]:mt-0 [&>div>*:last-child]:mb-0">
                              <h2 className="mb-4 font-[family-name:var(--featured-product-carousel-title-font-family,var(--font-family-heading))] text-2xl leading-none text-[var(--featured-product-carousel-title,hsl(var(--foreground)))]">
                                {descriptionTitle}
                              </h2>
                              {description}
                            </div>
                          </div>
                        )
                      }
                    </Stream>
                  </div>
                  <div className="group/product-warranty col-span-2">
                    {/* Warranty Section */}
                    <Stream fallback={<ProductWarrantySkeleton />} value={product.warranty}>
                      {(warranty) =>
                        Boolean(warranty) && (
                          <div className="group/product-warranty col-span-2">
                            <div className="prose max-w-none [&>div>*:first-child]:mt-0 [&>div>*:last-child]:mb-0">
                              <h2 className="mb-4 font-[family-name:var(--featured-product-carousel-title-font-family,var(--font-family-heading))] text-2xl leading-none text-[var(--featured-product-carousel-title,hsl(var(--foreground)))]">
                                {warrantyTitle}
                              </h2>
                              <div>
                                {warranty}
                              </div>
                            </div>
                          </div>
                        )
                      }
                    </Stream>
                  </div>
                </div>
              </>
            )
          }
        </Stream>
      </div>
    </section>
  );
}

function ProductGallerySkeleton() {
  return (
    <Skeleton.Root className="group-has-[[data-pending]]/product-gallery:animate-pulse" pending>
      <div className="w-full overflow-hidden rounded-xl @xl:rounded-2xl">
        <div className="flex">
          <Skeleton.Box className="aspect-[4/5] h-full w-full shrink-0 grow-0 basis-full" />
        </div>
      </div>
      <div className="mt-2 flex max-w-full gap-2 overflow-x-auto">
        {Array.from({ length: 5 }).map((_, idx) => (
          <Skeleton.Box className="h-12 w-12 shrink-0 rounded-lg @md:h-16 @md:w-16" key={idx} />
        ))}
      </div>
    </Skeleton.Root>
  );
}

function PriceLabelSkeleton() {
  return <Skeleton.Box className="my-5 h-4 w-20 rounded-md" />;
}

function RatingSkeleton() {
  return (
    <Skeleton.Root
      className="flex w-[136px] items-center gap-1 group-has-[[data-pending]]/product-rating:animate-pulse"
      pending
    >
      <Skeleton.Box className="h-4 w-[100px] rounded-md" />
      <Skeleton.Box className="h-6 w-8 rounded-xl" />
    </Skeleton.Root>
  );
}

function ProductSummarySkeleton() {
  return (
    <Skeleton.Root
      className="flex w-full flex-col gap-3.5 pb-6 group-has-[[data-pending]]/product-summary:animate-pulse"
      pending
    >
      {Array.from({ length: 3 }).map((_, idx) => (
        <Skeleton.Box className="h-2.5 w-full" key={idx} />
      ))}
    </Skeleton.Root>
  );
}

function ProductDescriptionSkeleton() {
  return (
    <Skeleton.Root
      className="flex w-full flex-col gap-3.5 pb-6 group-has-[[data-pending]]/product-description:animate-pulse"
      pending
    >
      {Array.from({ length: 2 }).map((_, idx) => (
        <Skeleton.Box className="h-2.5 w-full" key={idx} />
      ))}
      <Skeleton.Box className="h-2.5 w-3/4" />
    </Skeleton.Root>
  );
}

function ProductWarrantySkeleton() {
  return (
    <Skeleton.Root
      className="flex w-full flex-col gap-3.5 py-8 group-has-[[data-pending]]/product-warranty:animate-pulse border-t border-[var(--product-detail-border,hsl(var(--contrast-100)))]"
      pending
    >
      <Skeleton.Box className="h-8 w-32" />
      {Array.from({ length: 3 }).map((_, idx) => (
        <Skeleton.Box className="h-2.5 w-full" key={idx} />
      ))}
      <Skeleton.Box className="h-2.5 w-2/3" />
    </Skeleton.Root>
  );
}

function ProductDetailFormSkeleton() {
  return (
    <Skeleton.Root
      className="flex flex-col gap-8 py-8 group-has-[[data-pending]]/product-detail-form:animate-pulse"
      pending
    >
      <div className="flex flex-col gap-5">
        <Skeleton.Box className="h-2 w-10 rounded-md" />
        <div className="flex gap-2">
          {Array.from({ length: 3 }).map((_, idx) => (
            <Skeleton.Box className="h-11 w-[72px] rounded-full" key={idx} />
          ))}
        </div>
      </div>
      <div className="flex flex-col gap-5">
        <Skeleton.Box className="h-3 w-16 rounded-md" />
        <div className="flex gap-4">
          {Array.from({ length: 5 }).map((_, idx) => (
            <Skeleton.Box className="h-10 w-10 rounded-full" key={idx} />
          ))}
        </div>
      </div>
      <div className="flex gap-2">
        <Skeleton.Box className="h-12 w-[120px] rounded-lg" />
        <Skeleton.Box className="h-12 w-[216px] rounded-full" />
      </div>
    </Skeleton.Root>
  );
}

function ProductInfoGridSkeleton() {
  return (
    <Skeleton.Root
      className="flex h-[400px] w-full flex-col gap-6 pt-4 group-has-[[data-pending]]/product-info-grid:animate-pulse"
      pending
    >
      <div className="grid grid-cols-1 gap-6 @lg:grid-cols-2">
        {[1, 2, 3, 4].map((index) => (
          <div key={index} className="space-y-3">
            <Skeleton.Box className="h-6 w-32 rounded-sm" />
            <div className="space-y-2">
              <Skeleton.Box className="h-4 w-full rounded-sm" />
              <Skeleton.Box className="h-4 w-3/4 rounded-sm" />
              <Skeleton.Box className="h-4 w-1/2 rounded-sm" />
            </div>
          </div>
        ))}
      </div>
    </Skeleton.Root>
  );
}

export function ProductDetailSkeleton() {
  return (
    <Skeleton.Root
      className="grid grid-cols-1 items-stretch gap-x-6 gap-y-8 group-has-[[data-pending]]/product-detail:animate-pulse @2xl:grid-cols-2 @5xl:gap-x-12"
      pending
    >
      <div className="hidden @2xl:block">
        <ProductGallerySkeleton />
      </div>
      <div>
        <Skeleton.Box className="mb-6 h-4 w-20 rounded-lg" />
        <Skeleton.Box className="mb-6 h-6 w-72 rounded-lg" />
        <RatingSkeleton />
        <PriceLabelSkeleton />
        <ProductSummarySkeleton />
        <div className="mb-8 @2xl:hidden">
          <ProductGallerySkeleton />
        </div>
        <ProductDetailFormSkeleton />
      </div>
    </Skeleton.Root>
  );
}
